import { Box, Stack, styled, Tab } from '@mui/material';
import { DEFAULT_BACKGROUND_IMAGE_URL } from '@provision/membership-plans-ui';

export const CONFIG_PANEL_INITIAL_WIDTH_PX = 400;
export const CONFIG_PANEL_MIN_WIDTH_PX = 350;
export const CONFIG_PANEL_MAX_WIDTH_PX = 550;
const PREVIEW_MAX_WIDTH_PX = 400;

// Fills what the page header and tabs leave, so the panel's footer stays on screen.
export const MembershipsConfigContainer = styled(Stack)({
  flex: 1,
  minHeight: 0,
  position: 'relative',
  flexDirection: 'row',
});

export const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundImageUrl',
})<{ backgroundImageUrl?: string }>(({ backgroundImageUrl }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'auto',
  overflowX: 'hidden',
  backgroundImage: `url(${backgroundImageUrl || DEFAULT_BACKGROUND_IMAGE_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'scroll',
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: 0,
}));

export const LandingPagePreviewContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const MOBILE_VIEWPORT_WIDTH_PX = 390;

/** A real 390px viewport, not a scaled one, so the page's own media queries drive it. */
export const PreviewViewportFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ theme, isMobile }) => ({
  width: isMobile ? MOBILE_VIEWPORT_WIDTH_PX : '100%',
  maxWidth: '100%',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius,
  border: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
}));

export const PreviewContent = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  alignItems: 'center',
  justifyContent: 'flex-start',
  minHeight: 'min-content',
  width: '100%',
  paddingTop: theme.spacing(4),
  paddingBottom: 0,
  boxSizing: 'border-box',
}));

interface ConfigPanelProps {
  customWidth: number;
}

export const ConfigPanel = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'customWidth',
})<ConfigPanelProps>(({ theme, customWidth }) => ({
  width: theme.typography.pxToRem(customWidth),
  minWidth: theme.typography.pxToRem(CONFIG_PANEL_MIN_WIDTH_PX),
  maxWidth: theme.typography.pxToRem(CONFIG_PANEL_MAX_WIDTH_PX),
  flexShrink: 0,
  height: '100%',
  overflow: 'hidden',
}));

export const PanelHeader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
  borderBottom: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
}));

export const PanelContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  flex: 1,
  // Otherwise it grows to fit its content instead of scrolling, pushing the footer off screen.
  minHeight: 0,
  overflow: 'auto',
  // Hold the scrollbar's width whether or not it is showing, so expanding a block does not
  // reflow everything in the panel.
  scrollbarGutter: 'stable',
}));

export const PanelFooter = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
  borderTop: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
}));

export const PreviewPlaceholder = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(4),
  color: theme.palette.common.white,
  maxWidth: theme.typography.pxToRem(PREVIEW_MAX_WIDTH_PX),
  margin: '0 auto',
}));

export const MembershipsConfigTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  color: theme.palette.custom.OV.sys.onSurface,
  minHeight: theme.typography.pxToRem(48),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

export const PanelTitle = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 600,
  color: theme.palette.text.secondary,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
}));
